import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import CertificateRequirements from './CertificateRequirements';
import CertificatesList from './CertificatesList';
import CertificateForm from './CertificateForm';
import ArchiveCertificateModal from '../ArchiveCertificateModal';

const CertificatesSection = ({
  formData,
  errors,
  position,
  vesselAreaCategory,
  vesselEnginePower,
  vesselLength,
  vesselEquipment, // FT-30: Equipment config for conditional cert requirements
  setFormData
}) => {
  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [certificateToArchive, setCertificateToArchive] = useState(null);
  const [prefilledCertType, setPrefilledCertType] = useState(null);
  const [certOptions, setCertOptions] = useState(null);

  const handleAddCertificate = (certificate) => {
    setFormData(prev => ({
      ...prev,
      certificates: [...prev.certificates, { ...certificate, source: 'crew', isReadOnly: false }]
    }));
    setShowCertificateForm(false);
    setPrefilledCertType(null);
    setCertOptions(null);
  };

  const handleRemoveCertificate = (id) => {
    setFormData(prev => ({
      ...prev,
      certificates: prev.certificates.filter(cert => cert.id !== id)
    }));
  };

  // Handle click on a requirement to prefill the form
  const handleRequirementClick = (certType, options) => {
    if (options && options.length > 1) {
      // For COC or other multi-option certs, just use the first option for now
      // Could extend to show a selector modal in the future
      setPrefilledCertType(options[0]);
    } else {
      setPrefilledCertType(certType);
    }
    setShowCertificateForm(true);
    // Scroll to form
    setTimeout(() => {
      document.getElementById('certificate-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleClearPrefill = () => {
    setPrefilledCertType(null);
    setCertOptions(null);
  };

  // Open archive modal
  const handleArchiveCertificate = (certificate) => {
    setCertificateToArchive(certificate);
    setIsArchiveModalOpen(true);
  };

  // Confirm archive action - updates local form state
  // The actual persistence to archivedCertificates array happens on save
  const handleConfirmArchive = (archiveData) => {
    if (!certificateToArchive) return;

    setFormData(prev => ({
      ...prev,
      certificates: prev.certificates.map(cert =>
        cert.id === certificateToArchive.id
          ? {
              ...cert,
              status: 'archived',
              archivedAt: new Date().toISOString(),
              archivedBy: 'Current User', // In production, get from auth context
              reason: archiveData.reason,
              archiveNotes: archiveData.notes
            }
          : cert
      ),
      // Also maintain the archivedCertificates array for persistence
      archivedCertificates: [
        ...(prev.archivedCertificates || []),
        {
          certificateId: certificateToArchive.id,
          source: certificateToArchive.source || 'crew',
          archivedAt: new Date().toISOString(),
          archivedBy: 'Current User',
          reason: archiveData.reason,
          notes: archiveData.notes,
          originalData: {
            type: certificateToArchive.type,
            number: certificateToArchive.number,
            issue_date: certificateToArchive.issue_date,
            expiry_date: certificateToArchive.expiry_date
          }
        }
      ]
    }));

    setIsArchiveModalOpen(false);
    setCertificateToArchive(null);
  };

  // Unarchive a certificate
  const handleUnarchiveCertificate = (certificate) => {
    setFormData(prev => ({
      ...prev,
      certificates: prev.certificates.map(cert =>
        cert.id === certificate.id
          ? {
              ...cert,
              status: 'active',
              archivedAt: undefined,
              archivedBy: undefined,
              reason: undefined,
              archiveNotes: undefined
            }
          : cert
      ),
      // Remove from archivedCertificates array
      archivedCertificates: (prev.archivedCertificates || []).filter(
        a => a.certificateId !== certificate.id
      )
    }));
  };

  return (
    <div className="border-t border-gray-200 pt-6 space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Certificates & Training</h3>
        <p className="mt-1 text-sm text-gray-600">
          Required certificates based on position and operational area
        </p>
      </div>

      {/* Show required certificates */}
      <CertificateRequirements
        formData={formData}
        position={position}
        vesselAreaCategory={vesselAreaCategory}
        vesselEnginePower={vesselEnginePower}
        vesselLength={vesselLength}
        vesselEquipment={vesselEquipment}
        onRequirementClick={handleRequirementClick}
      />

      {/* Current certificates */}
      <CertificatesList
        certificates={formData.certificates}
        onRemoveCertificate={handleRemoveCertificate}
        onArchiveCertificate={handleArchiveCertificate}
        onUnarchiveCertificate={handleUnarchiveCertificate}
      />

      {/* Add certificate form */}
      {showCertificateForm ? (
        <div id="certificate-form">
          <CertificateForm
            onAddCertificate={handleAddCertificate}
            onCancel={() => {
              setShowCertificateForm(false);
              setPrefilledCertType(null);
              setCertOptions(null);
            }}
            existingCertificates={formData.certificates || []}
            prefilledType={prefilledCertType}
            onClearPrefill={handleClearPrefill}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowCertificateForm(true)}
          className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          <Plus className="h-4 w-4" />
          Add Certificate
        </button>
      )}

      {errors.certificates && (
        <p className="text-sm text-red-600">{errors.certificates}</p>
      )}

      {/* Archive Certificate Modal */}
      <ArchiveCertificateModal
        isOpen={isArchiveModalOpen}
        onClose={() => {
          setIsArchiveModalOpen(false);
          setCertificateToArchive(null);
        }}
        onConfirm={handleConfirmArchive}
        certificate={certificateToArchive}
      />
    </div>
  );
};

export default CertificatesSection;
